import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import Datepicker from 'components/ReusableComponents/Datepicker'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Toggle from 'components/ReusableComponents/Toggle'
import { inPropertyAudiences } from 'features/Announcements'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import { formatPrice } from 'utils/common'

import styles from './AnnouncementConfirmation.module.scss'

const SMS_COST = 0.03

const AnnouncementConfirmation = ({
  handleConfirm,
  handleCancel,
  audience,
  totalEligibleForText,
  announcementType
}) => {
  const audienceData = inPropertyAudiences[audience]
  const Icon = audienceData?.icon
  const title = audienceData?.name

  const [date, setDate] = useState(moment())
  const [isRecurring, setIsRecurring] = useState()
  const [repeatCount, setRepeatCount] = useState(0)

  const { addToast } = useToasts()

  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    setRepeatCount(isRecurring ? '1' : '0')
  }, [isRecurring])

  function handleSubmit() {
    setIsSubmitting(true)

    if (announcementType === 'schedule') {
      if (moment(date).isAfter(moment())) {
        handleConfirm({ date, repeatCount: Number(repeatCount) })
      } else {
        addToast('Please select a date & time in the future.', {
          appearance: 'error',
          autoDismiss: true
        })
        setIsSubmitting(false)
      }
    } else {
      handleConfirm({ repeatCount: 0 })
    }
  }

  useEffect(() => {
    if (announcementType === 'schedule') {
      setDate(moment().add(2, 'minutes').format())
    }
  }, [announcementType])

  function handleChangeHour(e) {
    const inputValue = e.target.value

    let hour = inputValue

    if (inputValue > 12) hour = 12

    if (inputValue < 1) hour = 1

    const fullDate = moment(date).format('YYYY-MM-DD')
    const minute = moment(date).format('mm')
    const am_pm = moment(date).format('A')

    setDate(`${fullDate} ${hour}:${minute} ${am_pm}`)
  }

  function handleChangeMinute(e) {
    const inputValue = e.target.value

    let minute = Number(inputValue)

    if (inputValue > 59) minute = 59

    if (inputValue < 0) minute = 0

    if (inputValue < 10) minute = `0${minute}`

    const fullDate = moment(date).format('YYYY-MM-DD')
    const hour = moment(date).format('h')
    const am_pm = moment(date).format('A')

    setDate(`${fullDate} ${hour}:${minute} ${am_pm}`)
  }

  function handleChangePeriod(e) {
    let am_pm = e.target.value

    const fullDate = moment(date).format('YYYY-MM-DD')
    const hour = moment(date).format('h')
    const minute = moment(date).format('mm')

    setDate(`${fullDate} ${hour}:${minute} ${am_pm}`)
  }

  function handleChangeDate(newDate) {
    const fullDate = moment(newDate).format('YYYY-MM-DD')
    const hour = moment(date).format('h')
    const minute = moment(date).format('mm')
    const am_pm = moment(date).format('A')

    setDate(`${fullDate} ${hour}:${minute} ${am_pm}`)
  }

  return (
    <ConfirmationDialog
      title={
        announcementType === 'schedule' ? 'Schedule announcement' : 'Confirm announcement'
      }
      onConfirm={() => handleSubmit()}
      onClose={handleCancel}
      negativeMessage="Cancel"
      positiveMessage="Confirm"
      contentPadding={0}
      isButtonDisabled={isSubmitting}
    >
      <div className={styles.summary} id="announcement-estimation">
        <div className={styles.row}>
          <div className={styles.audience}>
            <Icon className={styles.icon} />
            <div>
              <p className={styles.audienceName}>{title}</p>
              <p className={styles.audienceSize}>
                {announcementType === 'schedule' && <>Approx. </>}
                {totalEligibleForText} guests
              </p>
            </div>
          </div>
          <div className={styles.price}>
            <p className={styles.priceNumber}>
              {formatPrice(SMS_COST * totalEligibleForText * (repeatCount + 1))} approx.
            </p>
            <p className={styles.priceInfo}>${SMS_COST}/sms *</p>
          </div>
        </div>
        <div className={styles.row}>
          <div>
            <div className={styles.priceNote}>
              * Every month, first 500 messages are free of charge
            </div>
          </div>
        </div>
      </div>
      {announcementType === 'schedule' ? (
        <div>
          <div className={styles.content} style={{ width: 340, display: 'flex' }}>
            <div className={styles.timeHolder}>
              <p className={styles.label}>{isRecurring ? 'Start time' : 'Time'}</p>
              <div className={styles.time}>
                <TextInput
                  type="number"
                  min={1}
                  max={12}
                  value={moment(date).format('h')}
                  onChange={handleChangeHour}
                />
                <TextInput
                  type="number"
                  min={0}
                  max={60}
                  value={moment(date).format('mm')}
                  onChange={handleChangeMinute}
                />
                <Dropdown
                  options={{ AM: 'AM', PM: 'PM' }}
                  value={moment(date).format('A')}
                  onChange={handleChangePeriod}
                />
              </div>
            </div>
            <div className={styles.dateholder}>
              <div className={styles.date}>
                <p className={styles.label}>{isRecurring ? 'Start date' : 'Date'}</p>
                <Datepicker
                  selected={moment(date).toDate()}
                  className={styles.datepicker}
                  onChange={handleChangeDate}
                  minDate={moment().toDate()}
                />
              </div>
            </div>
          </div>
          <label className={styles.recurringToggleHolder}>
            <Toggle
              isOn={isRecurring}
              handleChange={e => setIsRecurring(e.target.checked)}
            />
            Repeat weekly
          </label>
          {isRecurring && (
            <div className={styles.repeatDropdownHolder}>
              <span className={styles.repeatLabel}>Repeat</span>
              <Dropdown
                defaultValue={'Repeat 1 time'}
                isSortedAlphabetically={false}
                options={Array(26)
                  .fill(null)
                  .reduce(
                    (acc, _, i) => ({
                      ...acc,
                      [i + 1]: `${i + 1} time${i > 0 ? 's' : ''}`
                    }),
                    {}
                  )}
                value={repeatCount}
                onChange={e => setRepeatCount(e.target.value)}
              />
            </div>
          )}
        </div>
      ) : (
        <div>
          <div className={styles.content}>
            <p className={styles.contentText}>Texts will be sent right now.</p>
          </div>
        </div>
      )}
    </ConfirmationDialog>
  )
}

export default AnnouncementConfirmation
